import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Bell, Calendar, Target, CheckCircle, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Check if we should enable WebSocket (skip in preview environments)
const WS_ENABLED = API_URL && !API_URL.includes('preview.emergentagent.com');

// Get WebSocket URL from API URL
const getWebSocketUrl = () => {
  if (!WS_ENABLED) return null;
  
  try {
    const url = new URL(API_URL);
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${url.host}/ws/notifications`;
  } catch {
    return null;
  }
};

const WebSocketContext = createContext({
  isConnected: false,
  lastMessage: null,
  sendMessage: () => {},
  reconnect: () => {}
});

export const useWebSocketNotifications = () => useContext(WebSocketContext);

export const WebSocketNotificationProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const getToken = () => {
    // Try to get token from localStorage or cookie
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const parsed = JSON.parse(user);
        return parsed.access_token || parsed.token;
      } catch {
        return null;
      }
    }
    return null;
  };

  const connect = useCallback(() => {
    const wsUrl = getWebSocketUrl();
    const token = getToken();
    
    if (!wsUrl || !token || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const ws = new WebSocket(`${wsUrl}?token=${token}`);

      ws.onopen = () => {
        setIsConnected(true);
        reconnectCountRef.current = 0;
        console.log('[WS] Connected to notifications');
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;
        
        // Attempt reconnection if not a clean close
        if (!event.wasClean && reconnectCountRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(3000 * Math.pow(1.5, reconnectCountRef.current), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectCountRef.current++;
            connect();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
          handleNotification(data);
        } catch (e) {
          console.error('[WS] Parse error:', e);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[WS] Connection error:', error);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  const sendHeartbeat = useCallback(() => {
    sendMessage({ type: 'heartbeat' });
  }, [sendMessage]);

  // Handle incoming notifications and show toasts
  const handleNotification = (data) => {
    const { type, notification_type, title, body } = data;

    if (type === 'notification' || type === 'appointment_update' || type === 'session_reminder' || type === 'progress_update') {
      const icon = getNotificationIcon(notification_type || type);
      
      toast(title || 'Notification', {
        description: body || data.details?.message,
        icon,
        duration: 5000,
        action: data.data?.action_url ? {
          label: 'View',
          onClick: () => window.location.href = data.data.action_url
        } : undefined
      });
    }

    if (type === 'pending_notifications' && data.count > 0) {
      toast(`You have ${data.count} unread notifications`, {
        icon: <Bell className="w-4 h-4" />,
        duration: 4000
      });
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment_update':
      case 'session_reminder':
        return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'progress_update':
      case 'milestone':
        return <Target className="w-4 h-4 text-purple-500" />;
      case 'goal_complete':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'no_show':
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  // Connect on mount if user is logged in
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user && WS_ENABLED) {
      connect();
    }

    // Setup heartbeat interval
    const heartbeatInterval = setInterval(sendHeartbeat, 30000);

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnected) {
        connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(heartbeatInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      disconnect();
    };
  }, []);

  // Reconnect when user logs in
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        if (e.newValue && WS_ENABLED) {
          connect();
        } else {
          disconnect();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connect, disconnect]);

  const value = {
    isConnected,
    lastMessage,
    sendMessage,
    reconnect: connect
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Connection status indicator component
export const ConnectionStatusIndicator = ({ className = '' }) => {
  const { isConnected } = useWebSocketNotifications();
  
  if (!WS_ENABLED) return null;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`} />
      <span className="text-xs text-gray-500">
        {isConnected ? 'Live' : 'Offline'}
      </span>
    </div>
  );
};

export default WebSocketNotificationProvider;
